import React, { useState, useRef, useEffect, useCallback } from 'react';
import NewtonSolverUI from './components/Methods/NewtonSolverUI';
import GaussSolverUI from './components/Methods/GaussSolverUI';
import InterpolationUI from './components/Methods/InterpolationUI';
import PolynomialFitUI from './components/Methods/PolynomialFitUI';
import DecompositionUI from './components/Methods/DecompositionUI';
import QuadratureUI from './components/Methods/QuadraturUI';
import IterativeLinearSolversUI from './components/Methods/IterativeLinearSolversUI';
import SimplexSolverUI from './components/Methods/SimplexSolverUI';
import LandingPage from './components/Pages/LandingPage';
import MethodPickerPage from './components/Pages/MethodPickerPage';

import GaussSolverExplanation from './components/Methods/GaussSolverExplanation';
import NewtonSolverExplanation from './components/Methods/NewtonSolverExplanation';
import InterpolationExplanation from './components/Methods/InterpolationExplanation';
import PolynomialFitExplanation from './components/Methods/PolynomialFitExplanation';
import MatrixDecompositionExplanation from './components/Methods/DecompositionExplanation';
import QuadratureExplanation from './components/Methods/QuadraturExplanation';
import IterativeLinearSolversExplanation from './components/Methods/IterativeLinearSolversExplanation';
import SimplexExplanation from './components/Methods/SimplexExplanation';

import GaussLongExplanation, { GaussIntro } from './components/LongExplanations/GaussLongExplanation';
import NewtonLongExplanation, { NewtonIntro } from './components/LongExplanations/NewtonLongExplanation';
import InterpolationLongExplanation, { InterpolationIntro } from './components/LongExplanations/InterpolationLongExplanation';
import LinearFitLongExplanation, { LinearFitIntro } from './components/LongExplanations/LinearFitLongExplanation';
import DecompositionLongExplanation, { DecompositionIntro } from './components/LongExplanations/DecompositionLongExplanation';
import QuadratureLongExplanation, { QuadratureIntro } from './components/LongExplanations/QuadratureLongExplanation';
import IterativeLinearSolversLongExplanation, { IterativeSolversIntro } from './components/LongExplanations/IterativeLinearSolversLongExplanation';
import SimplexLongExplanation, { SimplexIntro } from './components/LongExplanations/SimplexLongExplanation';

import NewtonIcon from './Icons/NewtonIcon';
import GaussIcon from './Icons/GaussIcon';
import InterpolationIcon from './Icons/InterpolationIcon';
import LinearFitIcon from './Icons/LinearFitIcon';
import DecompositionIcon from './Icons/DecompositionIcon';
import QuadratureIcon from './Icons/QuadratureIcon';
import IterativeLinearSolversIcon from './Icons/InterativeLinearSolversIcon';
import SimplexIcon from './Icons/SimplexIcon';

import GaussAnimation from './components/Animations/GaussAnimation';
import NewtonAnimation from './components/Animations/NewtonAnimation';
import InterpolationAnimation from './components/Animations/InterpolationAnimation';
import LinearFitAnimation from './components/Animations/LinearFitAnimation';
import DecompositionAnimation from './components/Animations/DecompositionAnimation';
import QuadratureAnimation from './components/Animations/QuadratureAnimation';
import IterativeLinearSolversAnimation from './components/Animations/IterativeLinearSolversAnimation';
import SimplexAnimation from './components/Animations/SimplexAnimation';


import MainLayout from './components/MainLayout';
import MethodCase from './components/Pages/MethodCase';
import RecentLogsPage from './components/Pages/RecentLogsPage';
import ContactPage from './components/Pages/ContactPage';
import PrivacyPolicyPage from './components/Pages/PrivacyPolicyPage';
import ImprintPage from './components/Pages/ImprintPage';
import BlogPage from './components/Pages/BlogPage';
import LiteraturePage from './components/Pages/LiteraturePage';

import { useFavicon } from './Icon';

const NUMSOLVE_BASE_DOMAIN = 'numsolve.org';

const SUBDOMAIN_CONFIG = {
  landing: ['landing', 'landingpage'],
  methodPicker: ['methodselection', 'methods', 'methodpicker'],
  explanations: ['explanations'],
  newton: ['newtonsmethod', 'newtons-method'],
  gauss: ['gaussianelimination', 'gaussian-elimination'],
  interpolation: ['interpolation'],
  linearfit: ['polynomialfit', 'ppolynomialfit', 'polynomial-fit'],
  decomposition: ['matrixdecomposition', 'matrix-decomposition'],
  iterlinsolve: ['iterlinsolve', 'iterative-linear-solvers'],
  quadrature: ['quadrature'],
  simplex: ['simplex'],
  recentLogs: ['recentlogs', 'recent-logs'],
  contact: ['contact'],
  privacy: ['privacypolicy', 'privacy-policy'],
  imprint: ['imprint'],
  blog: ['blog'],
  literature: ['literature'],
};

const DEFAULT_SUBDOMAIN_FOR_TAB = Object.fromEntries(
  Object.entries(SUBDOMAIN_CONFIG).map(([tab, subdomains]) => [tab, subdomains[0] ?? ''])
);

const SUBDOMAIN_TO_TAB = Object.entries(SUBDOMAIN_CONFIG).reduce(
  (acc, [tab, subdomains]) => {
    subdomains.forEach((subdomain) => {
      if (subdomain) {
        acc[subdomain.toLowerCase()] = tab;
      }
    });
    return acc;
  },
  {
    '': 'landing',
    www: 'landing',
  }
);

const VALID_TABS = new Set([
  'landing',
  'methodPicker',
  'explanations',
  'newton',
  'gauss',
  'interpolation',
  'linearfit',
  'decomposition',
  'iterlinsolve',
  'quadrature',
  'simplex',
  'recentLogs',
  'contact',
  'privacy',
  'imprint',
  'blog',
  'literature',
]);

const getHostname = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.hostname ?? '';
};

const isNumsolveDomain = () => false;

const getTabFromPathname = (pathname) => {
  const seg = (pathname || '/').replace(/\/+$/, '').split('/')[1] || '';
  return VALID_TABS.has(seg) ? seg : 'landing';
};

const buildPathForTab = (tab) => (tab === 'landing' ? '/' : `/${tab}`);

const getSubdomainFromHost = (hostname) => {
  if (!hostname) {
    return '';
  }

  const cleanHost = hostname.toLowerCase();
  const parts = cleanHost.split('.');

  if (parts.length <= 2) {
    return '';
  }

  return parts.slice(0, -2).join('.');
};

const getTabFromHostname = () => null;

const getTabFromSearch = (search) => {
  if (!search || typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(search);
  const tab = params.get('tab');

  if (!tab) {
    return null;
  }

  return VALID_TABS.has(tab) ? tab : null;
};

const getInitialTab = () => {
  if (typeof window === 'undefined') return 'landing';
  // Pfad hat Vorrang
  const fromPath = getTabFromPathname(window.location.pathname);
  if (fromPath) return fromPath;

  // optional: Query-Fallback (?tab=…)
  const fromQuery = getTabFromSearch(window.location.search);
  return fromQuery || 'landing';
};

const getHostForTab = (tab) => {
  const subdomain = DEFAULT_SUBDOMAIN_FOR_TAB[tab];
  if (typeof subdomain === 'undefined') {
    return null;
  }

  if (!subdomain) {
    return NUMSOLVE_BASE_DOMAIN;
  }

  return `${subdomain}.${NUMSOLVE_BASE_DOMAIN}`;
};

const buildLocalUrlForTab = (tab) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  if (tab === 'landing') {
    params.delete('tab');
  } else {
    params.set('tab', tab);
  }

  const search = params.toString();
  const path = window.location.pathname || '/';
  const hash = window.location.hash || '';

  return `${path}${search ? `?${search}` : ''}${hash}`;
};

const getLocationKey = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

function App() {

  useFavicon();

  const [activeTab, setActiveTabState] = useState(getInitialTab);
  const hostname = getHostname();
  const isNumsolveHost = isNumsolveDomain(hostname);

  useEffect(() => {
    if (typeof window === 'undefined' || !isNumsolveHost) {
      return undefined;
    }

    const desiredHost = getHostForTab(activeTab);
    if (!desiredHost) {
      return undefined;
    }

    const normalizedDesiredHost = desiredHost.toLowerCase();
    const currentHost = window.location.hostname.toLowerCase();

    if (normalizedDesiredHost !== currentHost) {
      const protocol = window.location.protocol || 'https:';
      window.location.replace(`${protocol}//${normalizedDesiredHost}/`);
    }

    return undefined;
  }, [activeTab, isNumsolveHost]);

  useEffect(() => {
  if (typeof window === 'undefined') return;

  const handlePopState = () => {
    const nextTab = getTabFromPathname(window.location.pathname) || 'landing';
    setActiveTabState((prev) => (prev === nextTab ? prev : nextTab));
  };

  window.addEventListener('popstate', handlePopState);
  return () => window.removeEventListener('popstate', handlePopState);
}, []);

const handleSetActiveTab = useCallback((tab) => {
  if (!VALID_TABS.has(tab)) return;

  if (tab !== activeTab) {
    setActiveTabState(tab);
  }
  if (typeof window === 'undefined') return;

  const newUrl = buildPathForTab(tab);
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (newUrl !== current) {
    window.history.pushState({ tab }, '', newUrl);
  }
}, [activeTab]);


  const [gaussState, setGaussState] = useState({
    showExplanation: false,
  });
  const [newtonState, setNewtonState] = useState({
    showExplanation: false,
  });
  const [interpolationState, setInterpolationState] = useState({
    showExplanation: false,
  });
  const [linearfitState, setLinearfitState] = useState({
    showExplanation: false,
  });
  const [decompositionState, setDecompositionState] = useState({
    showExplanation: false,
    shift: '0',
  });
  const [quadratureState, setQuadratureState] = useState({
    showExplanation: false,
  });
  const [simplexState, setSimplexState] = useState({
    showExplanation: false,
    useNormalForm: true,
    direction: 'max',
    objectiveInput: '',
    matrixInput: '',
    rhsInput: '',
    relationInput: '',
  });
  const [iterativeState, setIterativeState] = useState({
    showExplanation: false,
    method: 'jacobi',
    maxIterations: '',
    tolerance: '',
    omega: '',
    initialVectorInput: '',
    initialVectorError: false,
  });

  const METHOD_KEYS = ['newton', 'gauss', 'interpolation', 'linearfit', 'decomposition', 'iterlinsolve', 'quadrature', 'simplex'];
  const isMethod = (k) => METHOD_KEYS.includes(k);
  const [prevMethod, setPrevMethod] = useState(null);
  const lastTabRef = useRef(activeTab);
  const exitTimerRef = useRef(null);

  const mapLogMethodToTab = (method = '') => {
    const key = method.toLowerCase();
    if (key === 'linearfit' || key === 'polynomialfit') return 'linearfit';
    if (key.startsWith('decomposition') || key === 'qr') return 'decomposition';
    if (key === 'iterlinsolve' || key === 'iterative linear solvers') return 'iterlinsolve';
    if (key === 'simplex') return 'simplex';
    return key;
  };

  const stringifyInput = (value, spaces = 0) => {
    if (value === null || typeof value === 'undefined') return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, spaces);
    } catch {
      return String(value);
    }
  };

  const handleRerun = (log) => {
    if (!log) return;

    const input = log.input || {};
    const methodKey = mapLogMethodToTab(log.method || '');
    let targetTab = null;

    switch (methodKey) {
      case 'gauss': {
        const matrix = stringifyInput(input.matrix, 2);
        const vector = stringifyInput(input.vector, 2);
        setGaussState((prev) => ({
          ...prev,
          matrixInput: matrix,
          vectorInput: vector,
          result: null,
          error: null,
          loading: false,
          hasMatrixError: false,
          hasVectorError: false,
          lastUsedMatrix: matrix,
          lastUsedVector: vector,
        }));
        targetTab = 'gauss';
        break;
      }
      case 'newton': {
        const dampingValue = input.damping_value ?? input.dampingValue;
        const maxIterations = input.max_iterations ?? input.maxIterations;
        setNewtonState((prev) => ({
          ...prev,
          equation: input.function ?? prev.equation ?? '',
          startValue: stringifyInput(input.start),
          method: input.method ?? prev.method ?? '',
          damping: Boolean(input.damping),
          dampingValue: dampingValue !== undefined ? String(dampingValue) : (prev.dampingValue ?? '1.0'),
          tolerance: input.tolerance !== undefined ? String(input.tolerance) : (prev.tolerance ?? '1e-8'),
          maxIter: maxIterations !== undefined ? String(maxIterations) : (prev.maxIter ?? '20'),
          result: null,
          error: null,
          loading: false,
        }));
        targetTab = 'newton';
        break;
      }
      case 'interpolation': {
        setInterpolationState((prev) => ({
          ...prev,
          pointsInput: stringifyInput(input.points, 2),
          method: input.method ?? prev.method ?? '',
          splineType: input.spline_type ?? input.splineType ?? prev.splineType ?? '',
          result: null,
          error: null,
          plotData: [],
          loading: false,
        }));
        targetTab = 'interpolation';
        break;
      }
      case 'linearfit': {
        setLinearfitState((prev) => ({
          ...prev,
          pointsInput: stringifyInput(input.points, 2),
          degree: input.degree ?? prev.degree ?? 1,
          result: null,
          error: null,
          plotData: [],
          loading: false,
        }));
        targetTab = 'linearfit';
        break;
      }
      case 'decomposition': {
        const method = (input.method || log.method || '').toLowerCase();
        let derivedMethod = '';
        if (method.includes('lu')) derivedMethod = 'lu';
        else if (method.includes('chol')) derivedMethod = 'cholesky';
        else if (method.includes('qr')) derivedMethod = 'qr';

        setDecompositionState((prev) => ({
          ...prev,
          matrixInput: stringifyInput(input.matrix, 2),
          pivoting: Boolean(input.pivoting),
          activeMethod: derivedMethod || prev.activeMethod || '',
          iterate: typeof input.iterate === 'boolean' ? input.iterate : prev.iterate,
          maxIter: typeof input.max_iter !== 'undefined' ? input.max_iter : prev.maxIter,
          tolerance: typeof input.tolerance !== 'undefined' ? input.tolerance : prev.tolerance,
          shift: input.shift !== undefined && input.shift !== null
            ? String(input.shift)
            : (prev.shift ?? '0'),
          result: null,
          error: null,
          loading: false,
        }));
        targetTab = 'decomposition';
        break;
      }
      case 'iterlinsolve': {
        const selectedMethod = (input.method || log.method || '').toLowerCase();
        setIterativeState((prev) => ({
          ...prev,
          matrixInput: stringifyInput(input.matrix, 2),
          vectorInput: stringifyInput(input.vector, 2),
          method: selectedMethod || prev.method || 'jacobi',
          maxIterations: input.max_iterations !== undefined ? String(input.max_iterations) : (prev.maxIterations ?? '50'),
          tolerance: input.tolerance !== undefined ? String(input.tolerance) : (prev.tolerance ?? '1e-6'),
          omega: input.omega !== undefined ? String(input.omega) : (prev.omega ?? '1.0'),
          initialVectorInput: stringifyInput(
            input.initial_vector ?? input.initialVector ?? '',
            2,
          ),
          result: null,
          error: null,
          loading: false,
        }));
        targetTab = 'iterlinsolve';
        break;
      }
      case 'quadrature': {
        const subintervals = input.subintervals ?? input.n ?? null;
        setQuadratureState((prev) => ({
          ...prev,
          func: input.func ?? prev.func ?? '',
          a: input.a !== undefined ? String(input.a) : (prev.a ?? ''),
          b: input.b !== undefined ? String(input.b) : (prev.b ?? ''),
          method: input.method ?? prev.method ?? '',
          useSummedRule: Boolean(input.use_summed ?? input.useSummed),
          subintervals: subintervals !== null && subintervals !== undefined
            ? String(subintervals)
            : (prev.subintervals ?? '20'),
          result: null,
          error: null,
          loading: false,
        }));
        targetTab = 'quadrature';
        break;
      }
      case 'simplex': {
        const relations = Array.isArray(input.relations) ? input.relations : [];
        const normalizedRelations = relations.map((rel) => String(rel).trim());
        const isNormalForm =
          normalizedRelations.length > 0 &&
          normalizedRelations.every((rel) => rel === '<=' || rel === '≤');
        setSimplexState((prev) => ({
          ...prev,
          useNormalForm: isNormalForm,
          direction: input.maximize === false ? 'min' : 'max',
          objectiveInput: stringifyInput(input.objective, 2),
          matrixInput: stringifyInput(input.constraints, 2),
          rhsInput: stringifyInput(input.rhs, 2),
          relationInput: stringifyInput(input.relations, 2),
          result: null,
          error: null,
          loading: false,
        }));
        targetTab = 'simplex';
        break;
      }
      default:
        break;
    }

    if (targetTab) {
      handleSetActiveTab(targetTab);
    }
  };

  const METHOD_CONFIG = {
    newton: {
      title: 'Newton Solver',
      Icon: NewtonIcon,
      Intro: NewtonIntro,
      accentColor: '#34d399',
      UI: NewtonSolverUI,
      Explanation: NewtonSolverExplanation,
      LongExplanation: NewtonLongExplanation,
      Animation: NewtonAnimation,
      state: newtonState,
      setState: setNewtonState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
    gauss: {
      title: 'Gaussian Elimination',
      Icon: GaussIcon,
      Intro: GaussIntro,
      accentColor: '#60a5fa',
      UI: GaussSolverUI,
      Explanation: GaussSolverExplanation,
      LongExplanation: GaussLongExplanation,
      Animation: GaussAnimation,
      state: gaussState,
      setState: setGaussState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
    interpolation: {
      title: 'Interpolation',
      Icon: InterpolationIcon,
      Intro: InterpolationIntro,
      accentColor: '#c084fc',
      UI: InterpolationUI,
      Explanation: InterpolationExplanation,
      LongExplanation: InterpolationLongExplanation,
      Animation: InterpolationAnimation,
      state: interpolationState,
      setState: setInterpolationState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
    linearfit: {
      title: 'Polynomial Least Squares',
      Icon: LinearFitIcon,
      Intro: LinearFitIntro,
      accentColor: '#f59e0b',
      UI: PolynomialFitUI,
      Explanation: PolynomialFitExplanation,
      LongExplanation: LinearFitLongExplanation,
      Animation: LinearFitAnimation,
      state: linearfitState,
      setState: setLinearfitState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
    decomposition: {
      title: 'Matrix Decompositions',
      Icon: DecompositionIcon,
      Intro: DecompositionIntro,
      accentColor: '#818cf8',
      UI: DecompositionUI,
      Explanation: MatrixDecompositionExplanation,
      LongExplanation: DecompositionLongExplanation,
      Animation: DecompositionAnimation,
      state: decompositionState,
      setState: setDecompositionState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
    iterlinsolve: {
      title: 'Iterative Linear Solvers',
      Icon: IterativeLinearSolversIcon,
      Intro: IterativeSolversIntro,
      accentColor: '#38bdf8',
      UI: IterativeLinearSolversUI,
      Explanation: IterativeLinearSolversExplanation,
      LongExplanation: IterativeLinearSolversLongExplanation,
      Animation: IterativeLinearSolversAnimation,
      state: iterativeState,
      setState: setIterativeState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
    quadrature: {
      title: 'Numerical Quadrature',
      Icon: QuadratureIcon,
      Intro: QuadratureIntro,
      accentColor: '#fb7185',
      UI: QuadratureUI,
      Explanation: QuadratureExplanation,
      LongExplanation: QuadratureLongExplanation,
      Animation: QuadratureAnimation,
      state: quadratureState,
      setState: setQuadratureState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
    simplex: {
      title: 'Simplex Algorithm',
      Icon: SimplexIcon,
      Intro: SimplexIntro,
      accentColor: '#ef4444',
      UI: SimplexSolverUI,
      Explanation: SimplexExplanation,
      LongExplanation: SimplexLongExplanation,
      Animation: SimplexAnimation,
      state: simplexState,
      setState: setSimplexState,
      enterMs: 2200,
      flexWidth: 'w-full',
    },
  };


  const renderTab = () => {
    switch (activeTab) {
      case 'landing':
        return <LandingPage setActiveTab={handleSetActiveTab} />;
      case 'methodPicker':
        return <MethodPickerPage setActiveTab={handleSetActiveTab} />;
      case 'recentLogs':
        return <RecentLogsPage onRerun={handleRerun} />;
      case 'contact':
        return <ContactPage />;
      case 'privacy':
        return <PrivacyPolicyPage />;
      case 'imprint':
        return <ImprintPage />;
      case 'blog':
        return <BlogPage />;
      case 'literature':
        return <LiteraturePage />;

      default: {
        const cfg = METHOD_CONFIG[activeTab];
        if (!cfg) return null;

        const {
          UI, Explanation, state, setState,
          LongExplanation, Animation, enterMs, flexWidth,
          title, Intro,
          leftWidth = "w-full xl:flex-1 xl:min-w-0",
          colorClasses = "bg-white/80 border-white/40 text-slate-900 backdrop-blur-md",
          enterEase = "cubic-bezier(.25,.9,.2,1)",
          animationContainerClass = "bg-white/10 border-white/30 backdrop-blur-sm",
        } = cfg;

        return (
          <div className={`relative ${flexWidth}`}>
            {/* Aktuelles Panel */}
            <MethodCase
              key={activeTab}
              tabKey={activeTab}
              anchorEl={null}
              state={state}
              setState={setState}
              UI={UI}
              Explanation={Explanation}
              LongExplanation={LongExplanation}
              Animation={Animation}
              title={title}
              Intro={Intro}
              flexWidth="w-full"    // fills the wrapper
              leftWidth={leftWidth}
              colorClasses={colorClasses}
              enterMs={enterMs}
              enterEase={enterEase}
              animationContainerClass={animationContainerClass}
              visible={true}
            />

            {/* Previous panel as exit overlay (plays backward, takes no space) */}
            {prevMethod && prevMethod !== activeTab && (() => {
              const pcfg = METHOD_CONFIG[prevMethod];
              return (
                <MethodCase
                  key={`exit-${prevMethod}`}
                  tabKey={prevMethod}
                  anchorEl={null}
                  state={pcfg.state}
                  setState={pcfg.setState}
                  UI={pcfg.UI}
                  Explanation={pcfg.Explanation}
                  LongExplanation={pcfg.LongExplanation}
                  Animation={pcfg.Animation}
                  title={pcfg.title}
                  Intro={pcfg.Intro}
                  flexWidth="w-full"
                  leftWidth={pcfg.leftWidth ?? "w-full xl:flex-1 xl:min-w-0"}
                  colorClasses={
                    pcfg.colorClasses ??
                    "bg-white/80 border-white/40 text-slate-900 backdrop-blur-md"
                  }
                  enterMs={pcfg.enterMs}
                  enterEase={pcfg.enterEase ?? "cubic-bezier(.25,.9,.2,1)"}
                  animationContainerClass={
                    pcfg.animationContainerClass ?? "bg-white/10 border-white/30 backdrop-blur-sm"
                  }
                  asAbsolute={true}        // Overlay -> no layout shift
                  visible={false}          // Reverse (Exit)
                  initialEntered={true}    // starts fully open
                  exitMs={pcfg.enterMs}    // duration of return
                  hideExplanation={true}   // hide right side during exit
                />
              );
            })()}
          </div>
        );
      }

    }
  };

// Exit handling for method changes
useEffect(() => {
  const last = lastTabRef.current;
  // always update "last" but start exit first when the method changes
  if (last !== activeTab && isMethod(last) && isMethod(activeTab)) {
    setPrevMethod(last);
    // clear old timer if needed
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    const exitMs = (METHOD_CONFIG[last]?.enterMs ?? 1000);
    exitTimerRef.current = setTimeout(() => {
      setPrevMethod(null);
      exitTimerRef.current = null;
    }, exitMs);
  } else if (!isMethod(activeTab)) {
    // switch to landing: end old exit
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    setPrevMethod(null);
  }
  // IMPORTANT: always update lastTabRef; no early return!
  lastTabRef.current = activeTab;
}, [activeTab]);

  return (
    <>
      <MainLayout
        activeTab={activeTab}
        setActiveTab={handleSetActiveTab}
      >
        {activeTab === 'landing'
          ? renderTab()
          : renderTab()
        }
      </MainLayout>

    </>
  );
}

export default App;
